/* Floating Add to Cart
   - Stays hidden until <body> has the Intelligems activation class
     AND the page's main ATC button is scrolled out of view.
   - The floating button proxies a click to the real ATC button so it
     reuses each template's native add-to-cart flow.
*/
(function () {
  "use strict";

  const bar = document.querySelector("[data-floating-atc]");
  if (!bar) return;

  const ACTIVATION_CLASS = bar.dataset.activationClass || "ig-floating-atc";
  const floatingBtn = bar.querySelector("[data-floating-atc-btn]");
  const priceEl = bar.querySelector("[data-floating-atc-price]");
  const PRICE_RE = /\$\s?[\d.,]+/;

  // Selectors to locate the page's main Add to Cart button, in priority order.
  // Note: Rebuy upsell buttons (.rebuy-button) are intentionally excluded.
  const MAIN_ATC_SELECTORS = [
    "[data-add-to-cart]", // product-form.liquid (standard buy box)
    "[data-sub-bundle-submit]", // buy_box_v2.liquid (subscription bundle)
    ".buy-box-submit", // buy_box.liquid
    ".bb3-submit", // buy_box_v3.liquid
    "[data-bbs-submit]", // buy_box_simple.liquid
    'form[action*="/cart/add"] [name="add"]', // generic fallback
    'form[action*="/cart/add"] button[type="submit"]',
  ];

  function findMainAtc() {
    for (const sel of MAIN_ATC_SELECTORS) {
      const el = document.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  // Preview override for QA: append ?floating_atc=1 to the URL to force-show
  // the bar without the Intelligems body class.
  let previewForced = false;
  try {
    previewForced =
      new URLSearchParams(window.location.search).get("floating_atc") === "1";
  } catch (e) {
    previewForced = false;
  }

  function isActivated() {
    return previewForced || document.body.classList.contains(ACTIVATION_CLASS);
  }

  let mainAtc = null;
  let mainAtcInView = true;

  function evaluate() {
    const show = isActivated() && mainAtc && !mainAtcInView;
    bar.classList.toggle("is-visible", !!show);
  }

  // Read the per-bottle price already displayed by the buy box for the
  // currently selected option/mode. Falls back to null if not found.
  function getBuyBoxPerBottle() {
    // buy_box_v2: selected option shows "$X / bottle" in [data-price-now]
    const v2 = document.querySelector(".sub-bundle-buybox");
    if (v2) {
      const checked = v2.querySelector("label[data-option] input:checked");
      const label = checked && checked.closest("label[data-option]");
      const now = label && label.querySelector("[data-price-now]");
      const txt = now && now.textContent.trim();
      if (txt && PRICE_RE.test(txt)) return txt;
    }

    // buy_box_v3: active mode shows per-bottle in [data-price-val] (+ suffix)
    const v3 = document.querySelector("[data-bb3-buybox]");
    if (v3) {
      const mode = v3.querySelector('input[name="bb3_mode"]:checked');
      const isOnetime = mode && /one|otp|time/i.test(mode.value || "");
      const el = isOnetime
        ? v3.querySelector("[data-onetime-total]")
        : v3.querySelector("[data-sub-total]");
      if (el) {
        const val = el.querySelector("[data-price-val]");
        const suffix = el.querySelector("[data-price-suffix]");
        const valTxt = val && val.textContent.trim();
        if (valTxt && PRICE_RE.test(valTxt)) {
          const suffixTxt = suffix ? suffix.textContent.trim() : "";
          return (valTxt + (suffixTxt ? " " + suffixTxt : "")).trim();
        }
      }
    }

    // buy_box_simple: selected option shows price in [data-bbs-sub-price] or [data-bbs-onetime-price]
    const bbs = document.querySelector("[data-bbs-buybox]");
    if (bbs) {
      const checked = bbs.querySelector("label.bbs-option input:checked");
      const label = checked && checked.closest("label.bbs-option");
      const isOnetime = checked && checked.value === "onetime";
      const now = label && (isOnetime ? label.querySelector("[data-bbs-onetime-price]") : label.querySelector("[data-bbs-sub-price]"));
      const suffix = label && label.querySelector(".bbs-price-suffix");
      const txt = now && now.textContent.trim();
      if (txt && PRICE_RE.test(txt)) {
        const suffixTxt = suffix ? suffix.textContent.trim() : "";
        return (txt + (suffixTxt ? " " + suffixTxt : "")).trim();
      }
    }

    return null;
  }

  // Update the floating price: prefer the buy box's per-bottle price,
  // otherwise mirror the total shown in the main ATC button.
  function syncPrice() {
    if (!priceEl) return;
    const perBottle = getBuyBoxPerBottle();
    if (perBottle) {
      priceEl.textContent = perBottle;
      return;
    }
    if (mainAtc) {
      const match = (mainAtc.textContent || "").match(PRICE_RE);
      if (match) priceEl.textContent = match[0].replace(/\s/g, "");
    }
  }

  function setup() {
    mainAtc = findMainAtc();
    if (!mainAtc) return false;

    // Mirror the main ATC disabled state (e.g. sold out)
    if (mainAtc.disabled) floatingBtn.classList.add("is-disabled");

    const observer = new IntersectionObserver(
      (entries) => {
        mainAtcInView = entries[0].isIntersecting;
        evaluate();
      },
      { threshold: 0 },
    );
    observer.observe(mainAtc);

    // Keep the floating price in sync. Watch the main ATC button (total)
    // and the buy box (per-bottle) for live updates.
    syncPrice();
    const priceObserver = new MutationObserver(() => syncPrice());
    priceObserver.observe(mainAtc, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    const buyBox = document.querySelector(
      ".sub-bundle-buybox, [data-bb3-buybox], [data-bbs-buybox]"
    );
    if (buyBox) {
      const bbObserver = new MutationObserver(() => syncPrice());
      bbObserver.observe(buyBox, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ["class", "checked"],
      });
      buyBox.addEventListener("change", () => syncPrice());
    }

    return true;
  }

  // Proxy the click to the real ATC button.
  floatingBtn.addEventListener("click", (e) => {
    e.preventDefault();
    const target = findMainAtc();
    if (!target || target.disabled) return;
    target.click();
  });

  // React to Intelligems adding/removing the activation class after load.
  const bodyObserver = new MutationObserver(evaluate);
  bodyObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ["class"],
  });

  function init() {
    // Main ATC may render late (some templates hydrate async); retry briefly.
    if (setup()) {
      evaluate();
      return;
    }
    let attempts = 0;
    const retry = setInterval(() => {
      attempts++;
      if (setup() || attempts >= 10) {
        clearInterval(retry);
        evaluate();
      }
    }, 300);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
